import React, { useState, useEffect, useCallback } from 'react';
import { X, Copy, Check, Globe, Code, Link, ArrowsClockwise, Pause, Play, Trash, GearSix, CaretDown, Lock, ArrowSquareOut, Warning, ChartLine } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { usePlan } from '../context/PlanContext';
import { BACKEND_URL } from '../config';

const APP_DOMAIN = BACKEND_URL?.replace('/api', '').replace('https://', '').replace('http://', '') || window.location.host;
const APP_URL = BACKEND_URL?.replace('/api', '') || window.location.origin;

const TABS = [
  { id: 'share_link', label: 'Share Link', icon: Link },
  { id: 'chat_bubble', label: 'Chat Bubble', icon: Globe },
  { id: 'inline', label: 'Inline Embed', icon: Code },
];

function CopyButton({ text, label }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(`${label || 'Code'} copied!`);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} data-testid={`copy-${(label || 'code').toLowerCase().replace(/\s/g, '-')}-btn`}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[rgba(255,255,255,0.06)] border border-[rgba(255,255,255,0.08)] text-xs text-white hover:bg-[rgba(255,255,255,0.10)] transition-colors">
      {copied ? <Check size={13} weight="bold" className="text-green-400" /> : <Copy size={13} weight="duotone" />}
      {copied ? 'Copied' : `Copy ${label || ''}`}
    </button>
  );
}

function TabButton({ active, icon: Icon, label, onClick, locked }) {
  return (
    <button onClick={onClick} data-testid={`tab-${label.toLowerCase().replace(/\s/g, '-')}`}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-medium transition-all ${
        active
          ? 'bg-[rgba(0,229,192,0.1)] text-[#00E5C0] border border-[rgba(0,229,192,0.25)]'
          : 'text-[#8b8fa3] hover:text-white hover:bg-[rgba(255,255,255,0.04)] border border-transparent'
      }`}>
      {locked ? <Lock size={13} weight="duotone" className="text-[#555]" /> : <Icon size={13} weight="duotone" />}
      {label}
    </button>
  );
}

export default function DeploymentModal({ agentId, agentName, isOpen, onClose }) {
  const { currentPlan } = usePlan();
  const [tab, setTab] = useState('share_link');
  const [deployments, setDeployments] = useState([]);
  const [activeDep, setActiveDep] = useState(null);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [localSettings, setLocalSettings] = useState({
    widget_title: agentName || '',
    welcome_message: 'Hi! How can I help you today?',
    theme: 'dark',
    bubble_position: 'bottom-right',
    inline_height: '600px',
    show_branding: true,
    allowed_domains: [],
    lead_capture: false,
  });
  const [domainsInput, setDomainsInput] = useState('');

  const authHeaders = useCallback(() => {
    const token = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith('session_token='))?.split('=')[1];
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [limitsRes, depsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/deployments/limits`, { credentials: 'include', headers: authHeaders() }),
        fetch(`${BACKEND_URL}/api/deployments/agent/${agentId}`, { credentials: 'include', headers: authHeaders() }),
      ]);
      if (limitsRes.ok) setLimits(await limitsRes.json());
      if (depsRes.ok) {
        const data = await depsRes.json();
        setDeployments(data.deployments || []);
        if (data.deployments?.length > 0) {
          const active = data.deployments.find(d => d.status === 'active') || data.deployments[0];
          setActiveDep(active);
          if (active.settings) {
            setLocalSettings(prev => ({ ...prev, ...active.settings }));
            setDomainsInput((active.settings.allowed_domains || []).join(', '));
          }
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [agentId, authHeaders]);

  useEffect(() => {
    if (isOpen) fetchData();
  }, [isOpen, fetchData]);

  const createDeployment = async (types) => {
    setCreating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/deployments`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({ agent_id: agentId, deployment_types: types, settings: localSettings }),
      });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create deployment');
        return;
      }
      const data = await res.json();
      toast.success('Deployment created!');
      setActiveDep(data.deployment);
      setDeployments(prev => [data.deployment, ...prev]);
    } catch (err) {
      toast.error('Failed to create deployment');
    } finally {
      setCreating(false);
    }
  };

  const updateSettings = async () => {
    if (!activeDep) return;
    const domains = domainsInput.split(',').map(d => d.trim()).filter(Boolean);
    const updated = { ...localSettings, allowed_domains: domains };
    try {
      const res = await fetch(`${BACKEND_URL}/api/deployments/${activeDep.deployment_id}`, {
        method: 'PUT', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({ settings: updated, deployment_types: [tab] }),
      });
      if (res.ok) {
        const data = await res.json();
        setActiveDep(data.deployment);
        toast.success('Settings saved');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch { toast.error('Failed to save settings'); }
  };

  const updateStatus = async (depId, status) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/deployments/${depId}/status`, {
        method: 'PUT', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        toast.success(`Deployment ${status}`);
        fetchData();
      }
    } catch { toast.error('Failed to update status'); }
  };

  const regenerateKey = async () => {
    if (!activeDep) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/deployments/${activeDep.deployment_id}/regenerate-key`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setActiveDep(prev => ({ ...prev, public_key: data.public_key }));
        toast.success('Key regenerated. Old embed codes will stop working.');
        fetchData();
      }
    } catch { toast.error('Failed to regenerate key'); }
  };

  if (!isOpen) return null;

  const plan = limits?.plan || currentPlan || 'basic';
  const lim = limits?.limits || { share_link: true, website_embed: false, max_active: 0, branding_required: true, advanced_settings: false };
  const isEmbedLocked = !lim.website_embed && (tab === 'chat_bubble' || tab === 'inline');

  const publicUrl = activeDep ? `${APP_URL}/public/agent/${activeDep.public_key}` : '';

  const chatBubbleSnippet = activeDep ? `<!-- Man & Machine Agent Widget -->
<script>
(function(){
  var d="${APP_URL}",k="${activeDep.public_key}";
  var s=document.createElement("script");
  s.src=d+"/embed/agent.js";
  s.onload=function(){ManMachineEmbed.init({deploymentKey:k,mode:"bubble",position:"${localSettings.bubble_position}"})};
  document.head.appendChild(s);
})();
</script>` : '';

  const inlineSnippet = activeDep ? `<!-- Man & Machine Agent Widget -->
<div id="mm-agent"></div>
<script>
(function(){
  var d="${APP_URL}",k="${activeDep.public_key}";
  var s=document.createElement("script");
  s.src=d+"/embed/agent.js";
  s.onload=function(){ManMachineEmbed.init({deploymentKey:k,mode:"inline",container:"#mm-agent",height:"${localSettings.inline_height}"})};
  document.head.appendChild(s);
})();
</script>` : '';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="deployment-modal-overlay">
      <div className="bg-[#111827] border border-[rgba(255,255,255,0.08)] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="deployment-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[rgba(255,255,255,0.08)]">
          <div>
            <h2 className="text-base font-semibold text-white">Deploy & Share Worker</h2>
            <p className="text-xs text-[#8b8fa3] mt-0.5">{agentName}</p>
          </div>
          <button onClick={onClose} className="text-[#555] hover:text-white transition-colors" data-testid="close-deployment-modal">
            <X size={18} weight="bold" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-[#00E5C0] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {/* Tab selector */}
            <div className="flex gap-2 flex-wrap" data-testid="deployment-type-tabs">
              {TABS.map(t => (
                <TabButton key={t.id} active={tab === t.id} icon={t.icon} label={t.label}
                  locked={!lim.share_link && t.id === 'share_link' || (!lim.website_embed && (t.id === 'chat_bubble' || t.id === 'inline'))}
                  onClick={() => setTab(t.id)} />
              ))}
            </div>

            {/* Locked state */}
            {isEmbedLocked && (
              <div className="rounded-xl border border-[rgba(249,115,22,0.2)] bg-[rgba(249,115,22,0.05)] p-5" data-testid="embed-locked-message">
                <div className="flex items-start gap-3">
                  <Lock size={18} weight="duotone" className="text-orange-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-white font-medium mb-1">Website embed not available on your plan</p>
                    <p className="text-xs text-[#8b8fa3]">{limits?.upgrade_message || 'Upgrade to embed agents on your website.'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Create or view deployment */}
            {!isEmbedLocked && !activeDep && (
              <button onClick={() => createDeployment([tab])} disabled={creating} data-testid="create-deployment-btn"
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#00E5C0] text-[#0B1220] text-sm font-semibold hover:bg-[#00D4B1] disabled:opacity-50 transition-all">
                {creating ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : null}
                Create Deployment
              </button>
            )}

            {/* Active deployment */}
            {!isEmbedLocked && activeDep && (
              <>
                {/* Status badge */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${activeDep.status === 'active' ? 'bg-green-400' : activeDep.status === 'paused' ? 'bg-yellow-400' : 'bg-red-400'}`} />
                    <span className="text-xs text-[#8b8fa3] capitalize">{activeDep.status}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {activeDep.status === 'active' && (
                      <button onClick={() => updateStatus(activeDep.deployment_id, 'paused')} data-testid="pause-deployment-btn"
                        className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs text-yellow-400 bg-[rgba(234,179,8,0.08)] hover:bg-[rgba(234,179,8,0.15)] transition-colors">
                        <Pause size={12} weight="duotone" /> Pause
                      </button>
                    )}
                    {activeDep.status === 'paused' && (
                      <button onClick={() => updateStatus(activeDep.deployment_id, 'active')} data-testid="resume-deployment-btn"
                        className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs text-green-400 bg-[rgba(16,185,129,0.08)] hover:bg-[rgba(16,185,129,0.15)] transition-colors">
                        <Play size={12} weight="duotone" /> Resume
                      </button>
                    )}
                    <button onClick={() => { if (window.confirm('Revoke this deployment? This action cannot be undone.')) updateStatus(activeDep.deployment_id, 'revoked'); }} data-testid="revoke-deployment-btn"
                      className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs text-red-400 bg-[rgba(239,68,68,0.08)] hover:bg-[rgba(239,68,68,0.15)] transition-colors">
                      <Trash size={12} weight="duotone" /> Revoke
                    </button>
                  </div>
                </div>

                {/* Output: URL / Embed Code */}
                {tab === 'share_link' && (
                  <div className="space-y-3" data-testid="share-link-output">
                    <label className="text-xs font-medium text-[#8b8fa3]">Public Share Link</label>
                    <div className="flex items-center gap-2">
                      <input readOnly value={publicUrl} className="flex-1 bg-[#1A1B1E] border border-[rgba(255,255,255,0.08)] rounded-lg px-3 py-2 text-xs text-white font-mono" data-testid="public-url-input" />
                      <CopyButton text={publicUrl} label="Link" />
                      <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="p-2 rounded-md bg-[rgba(255,255,255,0.06)] text-white hover:bg-[rgba(255,255,255,0.10)] transition-colors">
                        <ArrowSquareOut size={14} weight="duotone" />
                      </a>
                    </div>
                  </div>
                )}

                {tab === 'chat_bubble' && (
                  <div className="space-y-3" data-testid="chat-bubble-output">
                    <label className="text-xs font-medium text-[#8b8fa3]">Chat Bubble Embed Code</label>
                    <pre className="bg-[#161B26] border border-[rgba(255,255,255,0.08)] rounded-lg p-4 text-xs text-[#c0c4d0] font-mono overflow-x-auto whitespace-pre-wrap" data-testid="embed-code-preview">
                      {chatBubbleSnippet}
                    </pre>
                    <div className="flex items-center gap-2">
                      <CopyButton text={chatBubbleSnippet} label="Code" />
                    </div>
                    <p className="text-[10px] text-[#555]">Paste this code before the closing &lt;/body&gt; tag of your website. Works with WordPress, Webflow, Squarespace, and custom HTML.</p>
                  </div>
                )}

                {tab === 'inline' && (
                  <div className="space-y-3" data-testid="inline-embed-output">
                    <label className="text-xs font-medium text-[#8b8fa3]">Inline Embed Code</label>
                    <pre className="bg-[#161B26] border border-[rgba(255,255,255,0.08)] rounded-lg p-4 text-xs text-[#c0c4d0] font-mono overflow-x-auto whitespace-pre-wrap" data-testid="embed-code-preview">
                      {inlineSnippet}
                    </pre>
                    <div className="flex items-center gap-2">
                      <CopyButton text={inlineSnippet} label="Code" />
                    </div>
                    <p className="text-[10px] text-[#555]">Place the &lt;div&gt; where you want the agent to appear. The script will mount the agent into that container.</p>
                  </div>
                )}

                {/* GearSix toggle */}
                <button onClick={() => setSettingsOpen(!settingsOpen)} data-testid="toggle-settings-btn"
                  className="flex items-center gap-2 w-full px-4 py-3 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] text-sm text-white hover:bg-[rgba(255,255,255,0.05)] transition-colors">
                  <GearSix size={14} weight="duotone" className="text-[#8b8fa3]" />
                  <span className="flex-1 text-left">Deployment Settings</span>
                  <CaretDown size={14} className={`text-[#555] transition-transform ${settingsOpen ? 'rotate-180' : ''}`} />
                </button>

                {settingsOpen && (
                  <div className="space-y-4 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] p-5" data-testid="deployment-settings">
                    {/* Widget title */}
                    <div>
                      <label className="text-xs text-[#8b8fa3] block mb-1">Widget Title</label>
                      <input value={localSettings.widget_title} onChange={e => setLocalSettings(s => ({...s, widget_title: e.target.value}))}
                        className="w-full bg-[#1A1B1E] border border-[rgba(255,255,255,0.08)] rounded-lg px-3 py-2 text-xs text-white" data-testid="setting-widget-title" />
                    </div>

                    {/* Welcome message */}
                    <div>
                      <label className="text-xs text-[#8b8fa3] block mb-1">Welcome Message</label>
                      <textarea value={localSettings.welcome_message} onChange={e => setLocalSettings(s => ({...s, welcome_message: e.target.value}))}
                        rows={2} className="w-full bg-[#1A1B1E] border border-[rgba(255,255,255,0.08)] rounded-lg px-3 py-2 text-xs text-white resize-none" data-testid="setting-welcome-message" />
                    </div>

                    {/* Theme */}
                    <div>
                      <label className="text-xs text-[#8b8fa3] block mb-1">Theme</label>
                      <div className="flex gap-2">
                        {['dark', 'light', 'auto'].map(t => (
                          <button key={t} onClick={() => setLocalSettings(s => ({...s, theme: t}))} data-testid={`theme-${t}`}
                            className={`px-3 py-1.5 rounded-lg text-xs capitalize ${localSettings.theme === t ? 'bg-[rgba(0,229,192,0.1)] text-[#00E5C0] border border-[rgba(0,229,192,0.25)]' : 'text-[#8b8fa3] bg-[rgba(255,255,255,0.04)] border border-transparent'}`}>
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Bubble position (only for chat_bubble) */}
                    {tab === 'chat_bubble' && (
                      <div>
                        <label className="text-xs text-[#8b8fa3] block mb-1">Bubble Position</label>
                        <div className="flex gap-2">
                          {['bottom-right', 'bottom-left'].map(p => (
                            <button key={p} onClick={() => setLocalSettings(s => ({...s, bubble_position: p}))} data-testid={`position-${p}`}
                              className={`px-3 py-1.5 rounded-lg text-xs capitalize ${localSettings.bubble_position === p ? 'bg-[rgba(0,229,192,0.1)] text-[#00E5C0] border border-[rgba(0,229,192,0.25)]' : 'text-[#8b8fa3] bg-[rgba(255,255,255,0.04)] border border-transparent'}`}>
                              {p.replace('-', ' ')}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Inline height (only for inline) */}
                    {tab === 'inline' && (
                      <div>
                        <label className="text-xs text-[#8b8fa3] block mb-1">Widget Height</label>
                        <input value={localSettings.inline_height} onChange={e => setLocalSettings(s => ({...s, inline_height: e.target.value}))}
                          placeholder="e.g. 600px" className="w-full bg-[#1A1B1E] border border-[rgba(255,255,255,0.08)] rounded-lg px-3 py-2 text-xs text-white" data-testid="setting-inline-height" />
                      </div>
                    )}

                    {/* Show branding */}
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-[#8b8fa3]">Show "Powered by Man & Machine" branding</label>
                      {lim.branding_required ? (
                        <span className="text-[10px] text-[#555] flex items-center gap-1"><Lock size={10} weight="duotone" /> Required on your plan</span>
                      ) : (
                        <button onClick={() => setLocalSettings(s => ({...s, show_branding: !s.show_branding}))} data-testid="toggle-branding"
                          className={`w-10 h-5 rounded-full transition-colors relative ${localSettings.show_branding ? 'bg-[#00E5C0]' : 'bg-[#333]'}`}>
                          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${localSettings.show_branding ? 'left-5' : 'left-0.5'}`} />
                        </button>
                      )}
                    </div>

                    {/* Allowed domains */}
                    <div>
                      <label className="text-xs text-[#8b8fa3] block mb-1">Allowed Domains <span className="text-[#555]">(comma-separated, leave empty for any)</span></label>
                      <input value={domainsInput} onChange={e => setDomainsInput(e.target.value)} placeholder="e.g. example.com, localhost"
                        className="w-full bg-[#1A1B1E] border border-[rgba(255,255,255,0.08)] rounded-lg px-3 py-2 text-xs text-white" data-testid="setting-allowed-domains" />
                    </div>

                    {/* Save button */}
                    <button onClick={updateSettings} data-testid="save-settings-btn"
                      className="w-full py-2.5 rounded-lg bg-[rgba(0,229,192,0.1)] text-[#00E5C0] text-xs font-medium hover:bg-[rgba(0,229,192,0.2)] transition-colors">
                      Save Settings
                    </button>
                  </div>
                )}

                {/* Regenerate key */}
                <div className="flex items-center justify-between pt-2">
                  <button onClick={regenerateKey} data-testid="regenerate-key-btn"
                    className="flex items-center gap-1.5 text-xs text-[#8b8fa3] hover:text-white transition-colors">
                    <ArrowsClockwise size={12} weight="duotone" /> Regenerate deployment key
                  </button>
                  {activeDep.analytics && (
                    <div className="flex items-center gap-3 text-[10px] text-[#555]" data-testid="deployment-stats">
                      <span>{activeDep.analytics.total_loads || 0} loads</span>
                      <span>{activeDep.analytics.conversations_started || 0} conversations</span>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Existing deployments list */}
            {deployments.length > 1 && (
              <div className="pt-3 border-t border-[rgba(255,255,255,0.08)]">
                <p className="text-xs text-[#8b8fa3] mb-2">All Deployments</p>
                <div className="space-y-2">
                  {deployments.map(dep => (
                    <div key={dep.deployment_id} onClick={() => {
                      setActiveDep(dep);
                      if (dep.settings) { setLocalSettings(prev => ({...prev, ...dep.settings})); setDomainsInput((dep.settings.allowed_domains || []).join(', ')); }
                    }}
                      data-testid={`deployment-item-${dep.deployment_id}`}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                        activeDep?.deployment_id === dep.deployment_id ? 'bg-[rgba(0,229,192,0.06)] border border-[rgba(0,229,192,0.15)]' : 'bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.04)]'
                      }`}>
                      <div className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${dep.status === 'active' ? 'bg-green-400' : dep.status === 'paused' ? 'bg-yellow-400' : 'bg-red-400'}`} />
                        <span className="text-xs text-white font-mono">{dep.public_key.substring(0, 20)}...</span>
                        <span className="text-[10px] text-[#555] capitalize">{dep.status}</span>
                      </div>
                      <span className="text-[10px] text-[#555]">{dep.deployment_types?.join(', ')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
